/* ============================================================
 * Melinda Maria — Cart Drawer Self-Heal
 * ------------------------------------------------------------
 * Workaround for a Horizon v3.2.1 cart drawer bug.
 *
 * Symptom: on the first cart action after a fresh page load
 * (qty change, line remove, etc.), the cart drawer collapses
 * to ~53px tall, showing only the first child of
 * cart-items-component (the GWP chip if present, otherwise the
 * .cart-drawer__header strip). The drawer's children are all
 * still in the DOM — the dialog itself has just lost its
 * modal state and collapsed to content height.
 *
 * Root cause: Horizon's section morph runs on the entire header
 * section (which contains the cart drawer dialog). The morph
 * preserves the dialog's `open` attribute but the act of
 * touching it kicks the dialog out of "modal" mode (the
 * top-layer state set by .showModal()). Once :modal stops
 * matching, the `:modal { max-height: 100dvh }` CSS rule in
 * snippets/header-actions.liquid no longer applies, and the
 * dialog collapses. After the first morph the dialog is
 * already non-modal, so subsequent morphs are no-ops on this
 * front and the bug doesn't recur.
 *
 * Fix: after every cart:update, check if the cart drawer dialog
 * is open but collapsed. If so, close it and reopen via
 * .showModal() to put it back in the top layer.
 *
 * Loaded unconditionally from layout/theme.liquid <head> — must
 * be present in the initial HTML (cart morph won't execute
 * script tags injected after page load).
 *
 * Remove this file when upgrading to Horizon v3.3+ — that
 * release added recreateAppBlockScripts() to morph.js which
 * may address the underlying bug.
 * ============================================================ */

(() => {
  if (window.__mmCartDrawerHealBound) return;
  window.__mmCartDrawerHealBound = true;

  // Healthy desktop drawer is viewport-tall (~600px+). Mobile is
  // 100dvh. 200px is a safe "obviously collapsed" threshold that
  // also covers small viewports without false positives.
  const COLLAPSED_HEIGHT_THRESHOLD = 200;

  let healing = false;

  function healDialog() {
    if (healing) return;

    const dialog = /** @type {HTMLDialogElement | null} */ (
      document.querySelector('cart-drawer-component dialog')
    );
    if (!dialog) return;
    if (!dialog.hasAttribute('open')) return;

    const rect = dialog.getBoundingClientRect();
    if (rect.height >= COLLAPSED_HEIGHT_THRESHOLD) return;

    // Confirm modal state was lost (don't re-show a healthy modal).
    if (dialog.matches(':modal')) return;

    healing = true;
    try {
      console.warn(
        '[mm_cart-drawer-heal] Drawer dialog collapsed (height=' +
          rect.height +
          'px, :modal=false). Re-opening as modal.'
      );

      // Suppress the slide-in flash by neutralizing the move-and-fade
      // keyframes. Horizon's .dialog-drawer[open] CSS rule animates
      // transform from translate(--start-x, --start-y) to translate(0, 0)
      // and opacity from --start-opacity to 1. By overriding those vars
      // inline so "from" equals "to", the animation runs but is a no-op.
      // We also cancel any in-flight animation just in case.
      dialog.style.setProperty('--start-x', '0px');
      dialog.style.setProperty('--start-y', '0px');
      dialog.style.setProperty('--start-opacity', '1');

      dialog.close();
      dialog.showModal();

      // Belt-and-suspenders: jump any animation Horizon's CSS started
      // straight to its end state. Future user-initiated opens will
      // animate normally because we restore the vars on the next frame.
      if (typeof dialog.getAnimations === 'function') {
        for (const anim of dialog.getAnimations()) anim.finish();
      }

      requestAnimationFrame(() => {
        dialog.style.removeProperty('--start-x');
        dialog.style.removeProperty('--start-y');
        dialog.style.removeProperty('--start-opacity');
      });
    } catch (err) {
      console.error('[mm_cart-drawer-heal] Failed to re-open dialog:', err);
      dialog.style.removeProperty('--start-x');
      dialog.style.removeProperty('--start-y');
      dialog.style.removeProperty('--start-opacity');
    } finally {
      healing = false;
    }
  }

  // Wait two animation frames so Horizon's morph + any layout
  // pass have completed before we measure.
  document.addEventListener('cart:update', () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(healDialog);
    });
  });
})();
